// LT-511: contact form shown after a slot is picked. Name + email (both
// required) + optional notes.
import React, { useState } from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import { Button, Input, Textarea } from '../../../ui';
import { endTime, formatDayHeading, slotTime } from '../formatters';

export interface BookingFormValues {
  name: string;
  email: string;
  notes: string;
}

interface Props {
  date: string;         // YYYY-MM-DD
  slotIso: string;      // UTC ISO
  timezone: string;
  durationMinutes: number;
  ownerName?: string | null;
  busy: boolean;
  inlineError?: string | null;
  onBack: () => void;
  onSubmit: (values: BookingFormValues) => void;
}

const BookingForm: React.FC<Props> = ({
  date, slotIso, timezone, durationMinutes, ownerName,
  busy, inlineError, onBack, onSubmit,
}) => {
  const [form, setForm] = useState<BookingFormValues>({ name: '', email: '', notes: '' });
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    if (!form.name.trim()) { setError('Enter your name.'); return; }
    if (!form.email.includes('@')) { setError('Enter a valid email.'); return; }
    setError(null);
    onSubmit(form);
  };

  const visibleError = error ?? inlineError;

  return (
    <div className="flex flex-col gap-5 bp-fade-up">
      <Button intent="ghost" size="sm" onClick={onBack} className="self-start -ml-2">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to times
      </Button>

      <div>
        <div className="text-[11px] font-medium uppercase tracking-wider text-slate-400 mb-1.5">
          Confirming
        </div>
        <div className="text-lg font-semibold text-slate-50">
          {formatDayHeading(date)}
        </div>
        <div
          className="text-sm text-accent-text mt-1"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {slotTime(slotIso, timezone)} — {endTime(slotIso, durationMinutes, timezone)}
        </div>
      </div>

      <div className="flex flex-col gap-3.5">
        <Input
          label="Your name"
          placeholder="Ada Lovelace"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          autoComplete="name"
        />
        <Input
          label="Email"
          type="email"
          placeholder="ada@example.com"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          autoComplete="email"
        />
        <Textarea
          label="What should we cover?"
          hint="(optional)"
          placeholder="A line or two about what you'd like to walk through."
          rows={3}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </div>

      {visibleError && (
        <div
          className="text-xs text-danger-text bg-danger-tint border border-danger-border px-3 py-2 rounded-ds-md"
          role="alert"
        >
          {visibleError}
        </div>
      )}

      <Button intent="primary" size="lg" disabled={busy} onClick={submit}>
        {busy ? 'Booking…' : 'Confirm booking'}
        {!busy && <Check className="w-3.5 h-3.5" />}
      </Button>

      {ownerName && (
        <div className="text-[11px] text-slate-500 leading-relaxed">
          By scheduling, you agree to share your name and email with{' '}
          {ownerName} for the purposes of this meeting.
        </div>
      )}
    </div>
  );
};

export default BookingForm;
